import React, { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { useI18n } from "../i18n";
import { BUSINESS_MODELS } from "../config/business-models";
import { PLANS, startingPrice } from "../config/plans";
import Icon from "../components/ui/Icon";

const NAV = [
  { href: "#features", th: "ความสามารถ", en: "Features" },
  { href: "#models", th: "โมเดลธุรกิจ", en: "Business models" },
  { href: "#pricing", th: "ราคา", en: "Pricing" },
  { href: "#faq", th: "คำถามที่พบบ่อย", en: "FAQ" },
];

const HERO_STATS = [
  { value: "18", th: "โมเดลธุรกิจ", en: "Business models" },
  { value: "18", th: "โมดูลพร้อมใช้", en: "Ready modules" },
  { value: "24/7", th: "รับชำระอัตโนมัติ", en: "Automated payments" },
];

const INTEGRATIONS = ["LINE OA", "Facebook", "Instagram", "PromptPay", "Google"];

const PRICING_FLOW = [
  { icon: "comment", th: "Social Media", en: "Social Media", sth: "FB / IG / LINE", sen: "FB / IG / LINE" },
  { icon: "users", th: "Lead เข้า CRM", en: "Leads into CRM", sth: "รวมทุกช่องทาง", sen: "Every channel" },
  { icon: "settings", th: "Auto Follow-up", en: "Auto Follow-up", sth: "& Assign Sales", sen: "& assign sales" },
  { icon: "chartBar", th: "ปิดการขาย", en: "Close the sale", sth: "เพิ่มยอดได้จริง", sen: "Real revenue growth" },
];

const PRICING_BENEFITS = [
  { icon: "chartBar", th: "เพิ่มยอดขาย", en: "Grow sales", sth: "จากการติดตามลูกค้าที่มีประสิทธิภาพ", sen: "with effective customer follow-up" },
  { icon: "users", th: "ลดภาระงานทีมขาย", en: "Lighten the sales team", sth: "ด้วยระบบอัตโนมัติ", sen: "with automation" },
  { icon: "heart", th: "สร้างความประทับใจให้ลูกค้า", en: "Delight customers", sth: "ด้วยการสื่อสารที่ตรงใจ", sen: "with communication that lands" },
  { icon: "success", th: "ข้อมูลปลอดภัย ได้มาตรฐาน", en: "Secure, standards-compliant data", sth: "รองรับการเติบโตของธุรกิจ", sen: "ready for business growth" },
];

const HUB_FEATURES = [
  { icon: "users", th: "ดูแลลูกค้า", en: "Customer care", dth: "เก็บประวัติลูกค้า การซื้อ และแชทไว้ในโปรไฟล์เดียว", den: "Keep history, purchases and chats in one profile." },
  { icon: "refresh", th: "เวิร์กโฟลว์", en: "Workflow", dth: "ไหลลื่นตั้งแต่ลีด นัดหมาย จนถึงปิดการขาย", den: "Flow from lead to appointment to closed deal." },
  { icon: "chartLine", th: "อินไซต์เชิงลึก", en: "Deep insights", dth: "เห็นรายได้ ยอดค้าง และคิววันนี้แบบเรียลไทม์", den: "Revenue, outstanding and today's queue in real time." },
  { icon: "card", th: "รับชำระเงิน", en: "Get paid", dth: "ออกบิลแล้วให้ลูกค้าสแกน QR PromptPay ได้ทันที", den: "Issue a bill, let customers scan QR PromptPay." },
];

const KEY_POINTS = [
  { th: "ติดตามลีด", en: "Lead tracking", dth: "จัดลำดับความสำคัญของลีด แล้วโฟกัสกับดีลที่มีโอกาสปิดจริง", den: "Prioritise leads and focus on deals that actually close." },
  { th: "นัดหมายและติดตามผล", en: "Follow-ups", dth: "ตั้งคิว แจ้งเตือนอัตโนมัติ ลดการลืมนัดและการยกเลิก", den: "Schedule and auto-remind — fewer no-shows." },
  { th: "เอกสารครบวงจร", en: "Documents built in", dth: "ใบเสนอราคา ใบแจ้งหนี้ ใบเสร็จ และสัญญาอิเล็กทรอนิกส์", den: "Quotations, invoices, receipts and e-contracts." },
];


const TESTIMONIALS = [
  {
    name: "คุณพิมพ์ชนก ภักดี",
    nameEn: "Pimchanok P.",
    role: "เจ้าของคลินิกความงาม",
    roleEn: "Aesthetic clinic owner",
    th: "เมื่อก่อนจดคิวในสมุด ลูกค้าลืมนัดบ่อยมาก พอย้ายมาใช้ระบบนี้ คิวไม่หลุดอีกเลย และรู้ทันทีว่าคอร์สลูกค้าเหลือกี่ครั้ง",
    en: "We used to track bookings on paper and customers kept forgetting. Now nothing slips, and we instantly see how many sessions are left.",
  },
  {
    name: "คุณธนกร รุ่งเรือง",
    nameEn: "Thanakorn R.",
    role: "ร้านเช่ามอเตอร์ไซค์",
    roleEn: "Motorbike rental",
    th: "ชอบตรงที่ดูได้ว่ารถคันไหนว่าง คันไหนถูกเช่าอยู่ แล้วออกสัญญาให้ลูกค้าเซ็นในจอได้เลย ไม่ต้องปริ้นท์",
    en: "I can see which bikes are free or rented, then have customers sign the contract on screen — no printing.",
  },
  {
    name: "คุณศิริพร จันทร์เพ็ญ",
    nameEn: "Siriporn J.",
    role: "แม่ค้าออนไลน์",
    roleEn: "Online seller",
    th: "ออเดอร์จากไลฟ์กับแชทเข้ามารวมที่เดียว ลูกค้าโอนแล้วส่งสลิป ระบบเช็คให้ ไม่ต้องไล่ดูยอดเองทีละคน",
    en: "Orders from live and chat land in one place. Customers send slips and the system checks them for me.",
  },
];

const FAQS = [
  {
    th: "ธุรกิจของฉันไม่ตรงกับ 18 โมเดล ใช้ได้ไหม?",
    en: "My business isn't one of the 18 models — can I still use it?",
    ath: "ใช้ได้ครับ โมเดลธุรกิจเป็นเพียงค่าตั้งต้นที่ช่วยเปิดโมดูลที่มักใช้คู่กัน คุณเปิด-ปิดโมดูลเองได้ทั้งหมดที่หน้าจัดการโมดูล",
    aen: "Yes. Business models are just presets that enable commonly paired modules — you can toggle every module yourself.",
  },
  {
    th: "รับชำระเงินได้ช่องทางไหนบ้าง?",
    en: "Which payment methods are supported?",
    ath: "รองรับ QR PromptPay ผ่านระบบชำระเงินจริง ใช้ได้ทั้งการจ่ายบิล มัดจำการจอง ตะกร้าสินค้า ขายหน้าร้าน และค่างวด รวมถึงรับแจ้งสลิปโอนเงินจากลูกค้า",
    aen: "QR PromptPay through a live payment gateway — for bills, booking deposits, carts, POS and installments, plus customer slip submissions.",
  },
  {
    th: "มีหลายสาขาต้องซื้อหลายระบบไหม?",
    en: "Do multiple branches need multiple systems?",
    ath: "ไม่ต้องครับ บัญชีเดียวจัดการได้หลายธุรกิจและหลายสาขา สลับดูข้อมูลรายสาขาหรือดูรวมทั้งหมดได้จากแถบด้านบน",
    aen: "No. One account manages multiple businesses and branches — switch or view everything from the top bar.",
  },
  {
    th: "ลูกค้าของฉันต้องโหลดแอปไหม?",
    en: "Do my customers need to install an app?",
    ath: "ไม่ต้องครับ ลูกค้าเปิดผ่านเว็บได้เลย ดูบิล จองคิว ดูสัญญา แจ้งโอนเงิน และแชทกับร้านได้จากหน้าเดียว",
    aen: "No. Customers use the web — bills, bookings, contracts, slip uploads and chat all in one place.",
  },
  {
    th: "ย้ายข้อมูลเดิมเข้าระบบได้ไหม?",
    en: "Can I migrate my existing data?",
    ath: "ได้ครับ ทีมงานช่วยนำเข้าข้อมูลลูกค้า รายการสินค้า และยอดค้างชำระให้ในช่วงเริ่มต้นใช้งาน",
    aen: "Yes — our team helps import customers, catalog and outstanding balances during onboarding.",
  },
];

const FOOTER = [
  {
    th: "ผลิตภัณฑ์",
    en: "Product",
    links: [
      { th: "ภาพรวมระบบ", en: "Overview" },
      { th: "ราคา", en: "Pricing" },
      { th: "โมเดลธุรกิจ", en: "Business models" },
      { th: "ทดลองใช้", en: "Demo" },
    ],
  },
  {
    th: "ความสามารถ",
    en: "Features",
    links: [
      { th: "CRM & ลูกค้า", en: "CRM" },
      { th: "จองคิว & นัดหมาย", en: "Booking" },
      { th: "ออกบิล & รับชำระ", en: "Billing" },
      { th: "ขายหน้าร้าน (POS)", en: "POS" },
    ],
  },
  {
    th: "บริษัท",
    en: "Company",
    links: [
      { th: "เกี่ยวกับเรา", en: "About us" },
      { th: "ร่วมงานกับเรา", en: "Careers" },
      { th: "ทีมงาน", en: "Team" },
      { th: "ติดต่อเรา", en: "Contact" },
    ],
  },
  {
    th: "ช่วยเหลือ",
    en: "Support",
    links: [
      { th: "ศูนย์ช่วยเหลือ", en: "Help center" },
      { th: "คู่มือเริ่มต้น", en: "Getting started" },
      { th: "สถานะระบบ", en: "System status" },
      { th: "แจ้งปัญหา", en: "Report an issue" },
    ],
  },
  {
    th: "แหล่งข้อมูล",
    en: "Resources",
    links: [
      { th: "บทความ", en: "Blog" },
      { th: "กรณีศึกษา", en: "Case studies" },
      { th: "สัมมนาออนไลน์", en: "Webinars" },
      { th: "อภิธานศัพท์", en: "Glossary" },
    ],
  },
];

export default function Landing() {
  const { isLoggedIn, isAdmin } = useApp();
  const { t, tx, pick } = useI18n();
  const [openFaq, setOpenFaq] = useState(0);
  const [navOpen, setNavOpen] = useState(false);

  if (isLoggedIn) return <Navigate to={isAdmin ? "/admin" : "/app"} replace />;

  return (
    <div className="lp">
      {/* ---------------- nav ---------------- */}
      <header className="lp-nav">
        <div className="lp-nav__inner">
          <Link to="/" className="lp-nav__brand">
            <span className="lp-mark">LU</span>
            LevelUpCRM
          </Link>
          <nav className="lp-nav__links">
            {NAV.map((item) => (
              <a key={item.href} href={item.href}>{tx(item.th, item.en)}</a>
            ))}
          </nav>
          <div className="lp-nav__actions">
            <Link to="/auth/login" className="lp-link">{t("action.login")}</Link>
            <Link to="/auth/register" className="lp-btn lp-btn--dark">{tx("สมัครใช้งาน", "Sign up")}</Link>
          </div>

          {/* ปุ่มเมนูสำหรับจอมือถือ */}
          <button type="button" className="lp-burger" onClick={() => setNavOpen((v) => !v)} aria-label="menu">
            <Icon name={navOpen ? "close" : "menu"} />
          </button>
        </div>

        {navOpen ? (
          <div className="lp-mobilemenu">
            {NAV.map((item) => (
              <a key={item.href} href={item.href} onClick={() => setNavOpen(false)}>{tx(item.th, item.en)}</a>
            ))}
            <Link to="/auth/login" onClick={() => setNavOpen(false)}>{t("action.login")}</Link>
            <Link to="/auth/register" className="lp-btn lp-btn--dark lp-btn--block" onClick={() => setNavOpen(false)}>
              {tx("สมัครใช้งาน", "Sign up")}
            </Link>
          </div>
        ) : null}
      </header>

      {/* ---------------- hero ---------------- */}
      <section className="lp-hero">
        <div className="lp-hero__glow lp-hero__glow--a" />
        <div className="lp-hero__glow lp-hero__glow--b" />
        <div className="lp-hero__glow lp-hero__glow--c" />

        <div className="lp-hero__inner">
          <h1>
            {tx("จัดการทุกส่วนของธุรกิจ", "Get a grip on your business")}
            <br />
            {tx("ได้จากที่เดียว ด้วย LevelUpCRM", "future with LevelUpCRM")}
          </h1>
          <p>
            {tx(
              "ซอฟต์แวร์ที่ใช้งานได้จริง ไม่ซับซ้อนเกินจำเป็น เชื่อมข้อมูลลูกค้า ทีมงาน และการเงินไว้บนแพลตฟอร์มเดียวที่โตไปพร้อมธุรกิจคุณ",
              "Software that's effective, not overbearing. Connect your data, team and customers on one platform that grows with your business."
            )}
          </p>

          <div className="lp-hero__cta">
            <Link to="/auth/register" className="lp-btn lp-btn--dark">{tx("เริ่มใช้งาน", "Get Started")}</Link>
            <a href="#features" className="lp-btn lp-btn--light">{tx("ดูรายละเอียด", "Learn more")}</a>
          </div>

          <div className="lp-social">
            <div className="lp-avatars">
              {["ก", "ธ", "ศ", "พ", "อ"].map((initial) => (
                <span className="lp-avatar" key={initial}>{initial}</span>
              ))}
            </div>
            <div>
              <div className="lp-stars">★★★★★</div>
              <div className="lp-social__text">{tx("มีธุรกิจกว่า 180 แห่งใช้งานอยู่", "Over 180 businesses have joined")}</div>
            </div>
          </div>

          <div className="lp-statbar">
            {HERO_STATS.map((stat, index) => (
              <React.Fragment key={stat.value + stat.th}>
                {index > 0 ? <span className="lp-statbar__sep" /> : null}
                <div className="lp-statbar__item">
                  <div className="lp-statbar__value">{stat.value}</div>
                  <div className="lp-statbar__label">{tx(stat.th, stat.en)}</div>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- integrations ---------------- */}
      <section className="lp-section lp-section--tight">
        <div className="lp-head">
          <h2>{tx("เชื่อมต่อกับเครื่องมือที่คุณใช้อยู่แล้ว", "Integrate with the tools you already use")}</h2>
          <p>{tx("ไม่ต้องเปลี่ยนวิธีทำงาน แค่เชื่อมช่องทางเดิมเข้ามาไว้ที่เดียว", "Keep your workflow — just connect your existing channels in one place.")}</p>
        </div>
        <div className="lp-logos">
          {INTEGRATIONS.map((name) => (
            <div className="lp-logo" key={name}>
              <span className="lp-logo__dot" />
              {name}
            </div>
          ))}
        </div>
      </section>

      {/* ---------------- payment card ---------------- */}
      <section className="lp-section">
        <div className="lp-paycard">
          <h3>{tx("รับเงินจากลูกค้าได้ทุกที่ ทุกเวลา", "Get money from your customers, anywhere")}</h3>
          <p>{tx("ออกบิลแล้วให้ลูกค้าสแกนจ่ายทันที ระบบตรวจสอบสถานะให้อัตโนมัติ", "Issue a bill, let them scan and pay — status is verified automatically.")}</p>

          <div className="lp-paycard__stage">
            <div className="lp-paycard__globe" />
            {[
              { label: "QR PromptPay", x: "6%", y: "24%", delay: "0s" },
              { label: "บัตรเครดิต", labelEn: "Credit card", x: "72%", y: "12%", delay: "1.2s" },
              { label: "โอนผ่านธนาคาร", labelEn: "Bank transfer", x: "78%", y: "58%", delay: "2.1s" },
              { label: "เงินสดหน้าร้าน", labelEn: "Cash at store", x: "14%", y: "64%", delay: "0.6s" },
              { label: "แจ้งสลิป", labelEn: "Slip upload", x: "44%", y: "72%", delay: "1.7s" },
            ].map((chip) => (
              <span className="lp-chip" key={chip.label} style={{ left: chip.x, top: chip.y, animationDelay: chip.delay }}>
                {tx(chip.label, chip.labelEn || chip.label)}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- hub ---------------- */}
      <section className="lp-section" id="features">
        <div className="lp-head">
          <h2>{tx("ศูนย์กลางธุรกิจที่ออกแบบมาเพื่อการเติบโต", "A thriving business hub designed for expansion")}</h2>
          <p>{tx("แพลตฟอร์มเดียวที่เชื่อมการตลาด การขาย การบริการ และการเงินเข้าด้วยกัน", "One platform connecting marketing, sales, service and finance.")}</p>
        </div>

        <div className="lp-hub">
          <div className="lp-hub__visual">
            <div className="lp-mockcard">
              <div className="lp-mockcard__head">
                <span className="lp-mockcard__title">{tx("คิววันนี้", "Today's queue")}</span>
                <span className="lp-pill lp-pill--mint">{tx("8 คิว", "8 slots")}</span>
              </div>
              {[
                { th: "ณัฐพล จันทร์เพ็ญ", en: "Nattapon C.", time: "10:15", tone: "mint" },
                { th: "พิมพ์ชนก ภักดี", en: "Pimchanok P.", time: "12:45", tone: "amber" },
                { th: "ธนกร รุ่งเรือง", en: "Thanakorn R.", time: "15:15", tone: "mint" },
              ].map((row) => (
                <div className="lp-mockrow" key={row.time}>
                  <span className="lp-mockrow__avatar">{tx(row.th, row.en).charAt(0)}</span>
                  <span className="lp-mockrow__name">{tx(row.th, row.en)}</span>
                  <span className="lp-mockrow__time">{row.time}</span>
                  <span className={`lp-dot lp-dot--${row.tone}`} />
                </div>
              ))}
            </div>
          </div>

          <div className="lp-hub__grid">
            {HUB_FEATURES.map((item) => (
              <div className="lp-fcard" key={item.th}>
                <span className="lp-fcard__icon"><Icon name={item.icon} /></span>
                <div className="lp-fcard__title">{tx(item.th, item.en)}</div>
                <div className="lp-fcard__detail">{tx(item.dth, item.den)}</div>
              </div>
            ))}
            <Link to="/auth/register" className="lp-btn lp-btn--dark lp-hub__cta">
              {tx("เริ่มสร้างระบบของคุณ", "Start building your business")}
            </Link>
          </div>
        </div>
      </section>

      {/* ---------------- key features ---------------- */}
      <section className="lp-section lp-section--tint">
        <div className="lp-head">
          <h2>{tx("ปลดล็อกศักยภาพธุรกิจด้วยฟีเจอร์หลัก", "Unlock the power of LevelUpCRM")}</h2>
          <p>{tx("เครื่องมืออัตโนมัติที่ช่วยให้ทีมทำงานน้อยลง แต่ดูแลลูกค้าได้ดีขึ้น", "Automation that lets your team do less and serve better.")}</p>
        </div>

        <div className="lp-keys">
          <div>
            <h3>{tx("จัดการลูกค้ามุ่งหวังอย่างเป็นระบบ ตั้งแต่ทักมาจนปิดการขาย", "Manage every potential customer, from first message to closed deal")}</h3>
            <p className="lp-keys__lead">
              {tx(
                "รวมลีดจากทุกช่องทางไว้บนกระดานเดียว มอบหมายผู้ดูแล ตั้งวันติดตามผล และแปลงเป็นใบเสนอราคาได้ทันที",
                "Collect leads from every channel on one board, assign owners, set follow-ups and turn them into quotations instantly."
              )}
            </p>
            <ul className="lp-bullets">
              {KEY_POINTS.map((point) => (
                <li key={point.th}>
                  <div className="lp-bullets__title">{tx(point.th, point.en)}</div>
                  <div className="lp-bullets__detail">{tx(point.dth, point.den)}</div>
                </li>
              ))}
            </ul>
          </div>

          <div className="lp-keys__visual">
            <div className="lp-mockboard">
              <div className="lp-mockboard__title">{tx("ไปป์ไลน์การขาย", "Sales pipeline")}</div>
              {[
                { th: "ใหม่", en: "New", value: 68, tone: "blue" },
                { th: "ติดต่อแล้ว", en: "Contacted", value: 52, tone: "lav" },
                { th: "กำลังเจรจา", en: "Negotiating", value: 34, tone: "pink" },
                { th: "ปิดการขาย", en: "Won", value: 21, tone: "mint" },
              ].map((row) => (
                <div className="lp-mockbar" key={row.en}>
                  <span className="lp-mockbar__label">{tx(row.th, row.en)}</span>
                  <span className="lp-mockbar__track">
                    <span className={`lp-mockbar__fill lp-mockbar__fill--${row.tone}`} style={{ width: `${row.value}%` }} />
                  </span>
                  <span className="lp-mockbar__value">{row.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- models ---------------- */}
      <section className="lp-section" id="models">
        <div className="lp-head">
          <h2>{tx("รองรับ 18 ประเภทธุรกิจ", "Built for 18 business types")}</h2>
          <p>{tx("เลือกโมเดลที่ใกล้เคียงธุรกิจคุณ ระบบเปิดโมดูลที่จำเป็นให้อัตโนมัติ", "Pick the closest model — required modules turn on automatically.")}</p>
        </div>
        <div className="lp-models">
          {BUSINESS_MODELS.map((model) => (
            <div className="lp-model" key={model.key}>
              <span className="lp-model__icon"><Icon name={model.icon} /></span>
              <span className="lp-model__name">{pick(model)}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ---------------- pricing ---------------- */}
      <section className="lp-section" id="pricing">
        <div className="lp-head lp-head--wide">
          <h2>{tx("CRM & Social Media Management Packages", "CRM & Social Media Management Packages")}</h2>
          <p>
            {tx(
              "เปลี่ยนทุก Lead จาก Social Media ให้กลายเป็นโอกาสทางการขาย พร้อมระบบติดตามลูกค้าอัตโนมัติ ครบวงจร",
              "Turn every social media lead into a sales opportunity, with end-to-end automated follow-up."
            )}
          </p>
          <p className="lp-head__note">{tx("ราคายังไม่รวม VAT 7% · จ่ายรายปีถูกกว่า ฟรี 2 เดือน · ไม่มีค่าติดตั้งแอบแฝง", "Excludes 7% VAT · pay yearly and get 2 months free · no hidden setup fees.")}</p>
        </div>

        <div className="lp-flow">
          {PRICING_FLOW.map((step, index) => (
            <React.Fragment key={step.en}>
              {index > 0 ? <span className="lp-flow__arrow">→</span> : null}
              <div className="lp-flow__step">
                <span className="lp-flow__icon"><Icon name={step.icon} /></span>
                <span className="lp-flow__name">{tx(step.th, step.en)}</span>
                <span className="lp-flow__sub">{tx(step.sth, step.sen)}</span>
              </div>
            </React.Fragment>
          ))}
        </div>

        <div className="lp-plans">
          {PLANS.map((plan) => (
            <div className={`lp-plan lp-plan--${plan.tone} ${plan.featured ? "is-featured" : ""}`} key={plan.key}>
              {plan.featured ? <span className="lp-plan__flag">{tx("ยอดนิยม", "Popular")}</span> : null}
              <div className="lp-plan__head">
                <div className="lp-plan__name">{tx(plan.name, plan.nameEn)}</div>
                <div className="lp-plan__detail">{tx(plan.detail, plan.detailEn)}</div>
                {plan.quoteOnly ? (
                  <div className="lp-plan__price">{tx("ตามใบเสนอราคา", "Custom quote")}</div>
                ) : (
                  <div className="lp-plan__price">
                    ฿{(plan.perUser ? plan.seatPrice : startingPrice(plan.key)).toLocaleString()}
                    <small>/{plan.perUser ? tx("User/เดือน", "user/mo") : tx("เดือน", "mo")}</small>
                    {plan.platformFee ? (
                      <small style={{ display: "block" }}>
                        + {tx("ค่า Platform", "platform fee")} ฿{plan.platformFee.toLocaleString()}/{tx("เดือน", "mo")}
                      </small>
                    ) : null}
                  </div>
                )}
                <div className="lp-plan__desc">{tx(plan.description, plan.descriptionEn)}</div>
              </div>
              <ul className="lp-plan__list">
                {plan.highlights.map((item) => (
                  <li key={item.en}>
                    <span className="lp-check">✓</span>
                    {tx(item.th, item.en)}
                  </li>
                ))}
              </ul>
              <Link to="/auth/register" className="lp-btn lp-btn--dark lp-btn--block">
                {tx("เริ่มใช้งาน", "Get Started")} <span className="lp-btn__chev">›</span>
              </Link>
            </div>
          ))}
        </div>

        <div className="lp-benefits">
          {PRICING_BENEFITS.map((item) => (
            <div className="lp-benefit" key={item.en}>
              <span className="lp-benefit__icon"><Icon name={item.icon} /></span>
              <span>
                <span className="lp-benefit__name">{tx(item.th, item.en)}</span>
                <span className="lp-benefit__sub">{tx(item.sth, item.sen)}</span>
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* ---------------- testimonials ---------------- */}
      <section className="lp-section">
        <div className="lp-head">
          <h2>{tx("เสียงจากผู้ใช้งานจริง", "What they said about LevelUpCRM")}</h2>
          <p>{tx("ธุรกิจหลากหลายประเภทที่เปลี่ยนวิธีทำงานด้วยระบบเดียว", "Businesses of every kind that changed how they work.")}</p>
        </div>

        <div className="lp-quotes">
          {TESTIMONIALS.map((item) => (
            <figure className="lp-quote" key={item.nameEn}>
              <div className="lp-quote__mark">”</div>
              <blockquote>{tx(item.th, item.en)}</blockquote>
              <figcaption>
                <span className="lp-quote__avatar">{tx(item.name, item.nameEn).charAt(0)}</span>
                <span>
                  <span className="lp-quote__name">{tx(item.name, item.nameEn)}</span>
                  <span className="lp-quote__role">{tx(item.role, item.roleEn)}</span>
                </span>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* ---------------- faq ---------------- */}
      <section className="lp-section" id="faq">
        <div className="lp-head">
          <h2>{tx("คำถามที่พบบ่อย", "Frequently asked questions")}</h2>
          <p>{tx("รวมคำถามที่เจ้าของธุรกิจถามเรามากที่สุดก่อนเริ่มใช้งาน", "The questions owners ask us most before getting started.")}</p>
        </div>

        <div className="lp-faq">
          {FAQS.map((faq, index) => (
            <div className={`lp-faq__item ${openFaq === index ? "is-open" : ""}`} key={faq.en}>
              <button type="button" className="lp-faq__q" onClick={() => setOpenFaq(openFaq === index ? -1 : index)}>
                <span>{tx(faq.th, faq.en)}</span>
                <span className="lp-faq__icon"><Icon name={openFaq === index ? "minus" : "plus"} /></span>
              </button>
              {openFaq === index ? <div className="lp-faq__a">{tx(faq.ath, faq.aen)}</div> : null}
            </div>
          ))}
        </div>
      </section>

      {/* ---------------- cta ---------------- */}
      <section className="lp-section">
        <div className="lp-cta">
          <h2>{tx("เริ่มต้นเส้นทางสู่ธุรกิจที่จัดการง่ายกว่าเดิม", "Embark on the journey to excellence")}</h2>
          <p>{tx("สมัครใช้งานฟรี ไม่ต้องผูกบัตรเครดิต ใช้ได้ทันทีหลังสมัคร", "Sign up free, no credit card, ready to use immediately.")}</p>
          <div className="lp-hero__cta">
            <Link to="/auth/register" className="lp-btn lp-btn--dark">{tx("เริ่มใช้งาน", "Get Started")}</Link>
            <a href="#pricing" className="lp-btn lp-btn--light">{tx("ดูราคา", "See pricing")}</a>
          </div>
        </div>
      </section>

      {/* ---------------- footer ---------------- */}
      <footer className="lp-footer">
        <div className="lp-footer__top">
          <h2>{tx("มีอะไรให้เราช่วยไหม? ติดต่อเราได้เลย", "How can we help? Contact us")}</h2>
        </div>

        <div className="lp-footer__cols">
          {FOOTER.map((col) => (
            <div key={col.en}>
              <div className="lp-footer__title">{tx(col.th, col.en)}</div>
              {col.links.map((link) => (
                <a href="#features" key={link.en}>{tx(link.th, link.en)}</a>
              ))}
            </div>
          ))}
        </div>

        <div className="lp-footer__mark">LEVELUP</div>

        <div className="lp-footer__bar">
          <span>© {new Date().getFullYear()} LevelUpCRM</span>
          <span>{tx("นโยบายความเป็นส่วนตัว · ข้อกำหนดการใช้งาน", "Privacy Policy · Terms and Conditions")}</span>
        </div>
      </footer>
    </div>
  );
}
